/* RESPONSIBLE TEAM: team-automation-foundations */
import Service, { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { timeout, type TaskGenerator } from 'ember-concurrency';
import generateUUID from 'embercom/lib/uuid-generator';
import { type AdminSummaryWireFormat } from 'embercom/objects/inbox/admin-summary';
import {
  NexusEventName,
  type WorkflowPresenceEventData,
  type AdminIsViewingWorkflowEvent,
  type AdminStoppedViewingWorkflowEvent,
} from 'embercom/services/nexus';
import type Nexus from 'embercom/services/nexus';
import type BpoAccessControlService from 'embercom/services/bpo-access-control';
import type Session from 'embercom/services/session';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import ENV from 'embercom/config/environment';
import { schedule } from '@ember/runloop';
// @ts-expect-error no type declarations for @intercom/pulse/lib/default-avatars
import DefaultAvatars from '@intercom/pulse/lib/default-avatars';

export const POLL_INTERVAL = ENV.environment === 'test' ? 10 : 30_000;

export interface WorkflowViewerInfo {
  id: number;
  name: string;
  imageURL: string | null;
  initial: string;
}

export default class RealtimeWorkflowParticipants extends Service {
  @service declare nexus: Nexus;
  @service declare bpoAccessControl: BpoAccessControlService;
  @service declare session: Session;
  @service declare store: Store;
  @service declare intl: IntlService;

  @tracked viewDataByWorkflow: {
    [key: string]: Record<string, WorkflowPresenceEventData>;
  } = {};
  private viewDataBySession: { [key: string]: WorkflowPresenceEventData } = {};

  private tasks: { [key: string]: any } = {};
  private sessionUUID: string;
  private currentAdmin?: { id: number; name: string; imageURL: string | null };
  private currentRulesetId?: number;

  constructor() {
    super(...arguments);
    this.sessionUUID = generateUUID();
    this.subscribeToEvents();
  }

  adminsViewingWorkflow(rulesetId: number): Array<WorkflowViewerInfo> {
    let workflowData = this.viewDataByWorkflow[rulesetId] || {};
    let uniqueByAdmin = new Map<number, WorkflowPresenceEventData>();

    for (let data of Object.values(workflowData)) {
      if (!uniqueByAdmin.has(data.admin.id)) {
        uniqueByAdmin.set(data.admin.id, data);
      }
    }

    return [...uniqueByAdmin.values()]
      .map((data) => this.resolveViewingAdmin(data))
      .filter((admin): admin is WorkflowViewerInfo => admin !== undefined);
  }

  viewingWorkflow(
    admin: { id: number; name: string; imageURL: string | null },
    rulesetId: number,
  ): void {
    this.currentAdmin = admin;
    this.currentRulesetId = rulesetId;

    let sessionUUID = this.sessionUUID;
    this.nexus.sendEvent(NexusEventName.AdminIsViewingWorkflow, {
      sessionUUID,
      workflowRulesetId: rulesetId,
      admin: this.serializeAdmin(admin),
    } as WorkflowPresenceEventData);
  }

  stopViewingWorkflow(
    admin: { id: number; name: string; imageURL: string | null },
    rulesetId: number,
  ): void {
    let sessionUUID = this.sessionUUID;
    this.nexus.sendEvent(NexusEventName.AdminStoppedViewingWorkflow, {
      sessionUUID,
      workflowRulesetId: rulesetId,
      admin: this.serializeAdmin(admin),
    } as WorkflowPresenceEventData);
  }

  private resolveViewingAdmin(data: WorkflowPresenceEventData): WorkflowViewerInfo | undefined {
    let admin = this.store.peekRecord('admin', String(data.admin.id)) as {
      name: string;
      avatar: { image_urls: { square_128: string } };
    } | null;

    let name =
      admin?.name || data.admin.name || this.intl.t('workflows.editor.fallback-admin-name');
    let imageURL = admin?.avatar?.image_urls?.square_128 || data.admin.image_url || null;

    return {
      id: data.admin.id,
      name,
      imageURL,
      initial: name.charAt(0).toUpperCase(),
    };
  }

  private serializeAdmin(admin: {
    id: number;
    name: string;
    imageURL: string | null;
  }): AdminSummaryWireFormat {
    let adminData: AdminSummaryWireFormat = {
      id: admin.id,
    };

    let shouldRedact =
      this.session.isWorkspaceLoaded &&
      this.session.workspace.isFeatureEnabled('hsg-bpo-separation-websocket-redaction');
    if (!shouldRedact) {
      adminData.name = admin.name;
      adminData.image_url = admin.imageURL;
    } else {
      adminData.name = this.intl.t('workflows.editor.fallback-admin-name');
      adminData.image_url = DefaultAvatars.admins[128];
      adminData.is_redacted = true;
    }

    return adminData;
  }

  private subscribeToEvents() {
    this.nexus.addListener(
      NexusEventName.AdminIsViewingWorkflow,
      (e: AdminIsViewingWorkflowEvent) => this.handleAdminIsViewingWorkflowEvent(e.eventData),
    );
    this.nexus.addListener(
      NexusEventName.AdminStoppedViewingWorkflow,
      (e: AdminStoppedViewingWorkflowEvent) =>
        this.handleAdminStoppedViewingWorkflowEvent(e.eventData),
    );
  }

  private handleAdminIsViewingWorkflowEvent(data: WorkflowPresenceEventData): void {
    schedule('actions', () => {
      if (this.sessionUUID === data.sessionUUID) {
        return;
      }

      if (!this.bpoAccessControl.adminAccessible(data.admin.id)) {
        return;
      }

      let isNewAdmin = !this.viewDataBySession[data.sessionUUID];
      this.tasks[data.sessionUUID]?.cancel();
      let runningTask = taskFor(this.trackAdminViewingWorkflow).perform(data);
      this.tasks[data.sessionUUID] = runningTask;

      if (isNewAdmin && this.currentAdmin && this.currentRulesetId !== undefined) {
        this.viewingWorkflow(this.currentAdmin, this.currentRulesetId);
      }
    });
  }

  private handleAdminStoppedViewingWorkflowEvent(data: WorkflowPresenceEventData): void {
    schedule('actions', () => {
      if (this.sessionUUID === data.sessionUUID) {
        return;
      }

      if (!this.bpoAccessControl.adminAccessible(data.admin.id)) {
        return;
      }

      this.tasks[data.sessionUUID]?.cancel();
      this.untrackDataForSession(data);
    });
  }

  @task
  private *trackAdminViewingWorkflow(data: WorkflowPresenceEventData): TaskGenerator<void> {
    this.trackDataForSession(data);
    yield timeout(POLL_INTERVAL * 2);
    this.untrackDataForSession(data);
  }

  private trackDataForSession(data: WorkflowPresenceEventData): void {
    let previousSessionData = this.viewDataBySession[data.sessionUUID];
    if (previousSessionData) {
      if (previousSessionData.workflowRulesetId === data.workflowRulesetId) {
        return;
      }
      this.untrackDataForSession(previousSessionData);
    }

    this.viewDataBySession[data.sessionUUID] = data;

    let workflowViewData = this.viewDataByWorkflow[data.workflowRulesetId] || {};
    workflowViewData[data.sessionUUID] = data;
    this.viewDataByWorkflow[data.workflowRulesetId] = workflowViewData;
    this.viewDataByWorkflow = this.viewDataByWorkflow;
  }

  private untrackDataForSession(data: WorkflowPresenceEventData): void {
    delete this.tasks[data.sessionUUID];

    let workflowViewData = this.viewDataByWorkflow[data.workflowRulesetId] || {};
    delete workflowViewData[data.sessionUUID];
    this.viewDataByWorkflow[data.workflowRulesetId] = workflowViewData;
    this.viewDataByWorkflow = this.viewDataByWorkflow;

    delete this.viewDataBySession[data.sessionUUID];
  }
}

declare module '@ember/service' {
  interface Registry {
    realtimeWorkflowParticipants: RealtimeWorkflowParticipants;
    'realtime-workflow-participants': RealtimeWorkflowParticipants;
  }
}
